import { useActorRef, useSelector } from '@xstate/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType } from 'react-router';
import { createEmptyActor } from 'xstate';
import { UserContext } from '~/app';
import { isNotificationArrival } from '~/features/notifications/constants';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { getVaultTopicIcon } from '~/routing/vaultTopicIcons';
import { PROVIDER_BASE } from '~/utils/constants';
import { selectVaultContext, selectVaultState } from '../account/vaultSelectors';
import { createForcedDocumentsRefresher } from '../documents/forcedDocumentsRefresh';
import { isUploadedToday } from '../documents/isUploadedToday';
import { sortPrivileges } from '../privileges/constants';
import type { DerivedStatus } from './boardCerts/deriveStatus';
import { deriveBoardCertStatus } from './boardCerts/deriveStatus';
import { EMPLOYMENT_GAP_CATEGORY_IDS, isPrivilegesApplicable } from './constants';
import {
  computeAlerts,
  computeCategoryStatus,
  computeCompleteness,
  computeExpirationStatus,
  computeQuickStats,
  detectEmploymentGaps,
  formatCredentialSummaryDate,
  getEarliestOriginalCertificationDate,
  getMostRecentAcademicRecords,
  getMostRecentEmploymentRecords,
  sortItemsByUrgency,
  type EmploymentRecord,
} from './dashboardUtils';
import { eduTrainingMachine } from './eduTraining/eduTraining.machine';
import { combinedLicenseMachine } from './licenses/license.machine';
import { NEW_RECORD_TABLE, idsOf } from './newRecordTables';
import type { AlertItem, CategorySummary, DashboardSummary, SummaryItem } from './types';

const emptyActor = createEmptyActor();

/**
 * "MM/YYYY" of an expiry date, read in UTC. A date-only string such as '2030-05-01' parses as UTC
 * midnight, so the local getters give April for it in every zone west of UTC. An expiry keeps
 * this numeric form on purpose: the year-only rule of MCV-1811 (formatCredentialSummaryDate, used
 * by formatGrad and formatEmploymentDate below) is for education, training and employment dates.
 */
const formatMonthYear = (d: Date) =>
  `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;

/** Maps raw category data into a CategorySummary for a category with expiration dates */
function buildExpirableCategory(
  key: string,
  name: string,
  route: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[],
  loading: boolean,
  error: boolean,
  nameField: string,
  expiryField: string,
  options: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deriveStatus?: (item: any) => DerivedStatus;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columnsForItem?: (item: any) => string[];
  } = {}
): CategorySummary {
  const { deriveStatus, columnsForItem } = options;
  let expiredCount = 0;
  let expiringCount = 0;
  let inactiveCount = 0;
  let unknownCount = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itemsWithExpiry = data.map((item: any) => {
    const rawName = typeof item[nameField] === 'object' ? item[nameField]?.label : item[nameField];
    const expiryDate = item[expiryField] || undefined;

    const derived: DerivedStatus = deriveStatus
      ? deriveStatus(item)
      : { status: computeExpirationStatus(expiryDate), lifetime: false };

    const { status, lifetime } = derived;

    if (status === 'expired') expiredCount++;
    else if (status === 'expiring') expiringCount++;
    else if (status === 'inactive') inactiveCount++;
    else if (status === 'unknown') unknownCount++;

    let badge = 'Active';
    let badgeVariant: SummaryItem['badgeVariant'] = 'good';
    if (status === 'expired') {
      badge = 'Expired';
      badgeVariant = 'error';
    } else if (status === 'inactive') {
      badge = 'Inactive';
      badgeVariant = 'warning';
    } else if (status === 'expiring' && expiryDate) {
      const d = new Date(expiryDate);
      badge = `Exp ${formatMonthYear(d)}`;
      badgeVariant = 'warning';
    } else if (status === 'unknown') {
      badge = 'No Expiry';
      badgeVariant = 'warning';
    } else if (status === 'active' && lifetime) {
      badge = 'No Expiration';
      badgeVariant = 'good';
    }

    const columns = columnsForItem ? columnsForItem(item) : undefined;
    return { name: rawName || '--', badge, badgeVariant, expiryDate, status, columns };
  });

  const sorted = sortItemsByUrgency(itemsWithExpiry);
  const status = computeCategoryStatus(expiredCount, expiringCount, unknownCount, inactiveCount);

  let statusText = 'All current';
  const parts: string[] = [];
  if (expiredCount > 0) parts.push(`${expiredCount} expired`);
  if (expiringCount > 0) parts.push(`${expiringCount} expiring`);
  if (inactiveCount > 0) parts.push(`${inactiveCount} inactive`);
  if (unknownCount > 0) parts.push(`${unknownCount} no expiry`);
  if (parts.length > 0) statusText = parts.join(', ');

  return {
    key,
    name,
    count: data.length,
    status,
    statusText,
    expiredCount,
    expiringCount,
    inactiveCount,
    items: sorted.map(({ name: n, badge, badgeVariant, columns }) => ({
      name: n,
      badge,
      badgeVariant,
      ...(columns ? { columns } : {}),
    })),
    loading,
    error,
    navigateTo: `${PROVIDER_BASE}/${route}`,
    icon: getVaultTopicIcon(route),
  };
}

/** Maps raw category data into a CategorySummary for a neutral category (no expiration) */
function buildNeutralCategory(
  key: string,
  name: string,
  route: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[],
  loading: boolean,
  error: boolean,
  nameField: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  badgeFormatter: (item: any) => string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columnsForItem?: (item: any) => string[],
  maxItems = 5
): CategorySummary {
  const items: SummaryItem[] = data.slice(0, maxItems).map((item) => {
    const rawName = typeof item[nameField] === 'object' ? item[nameField]?.label : item[nameField];
    const columns = columnsForItem ? columnsForItem(item) : undefined;
    return {
      name: rawName || '--',
      badge: badgeFormatter(item),
      badgeVariant: 'neutral' as const,
      ...(columns ? { columns } : {}),
    };
  });

  return {
    key,
    name,
    count: data.length,
    status: 'neutral',
    statusText: `${data.length} ${key === 'cmeTraining' ? (data.length === 1 ? 'credit logged' : 'credits logged') : key === 'education' ? (data.length === 1 ? 'record' : 'records') : 'uploaded'}`,
    expiredCount: 0,
    expiringCount: 0,
    inactiveCount: 0,
    items,
    loading,
    error,
    navigateTo: `${PROVIDER_BASE}/${route}`,
    icon: getVaultTopicIcon(route),
  };
}

export const useDashboardSummary = (): DashboardSummary => {
  const { vaultRef } = useSelector(UserContext.useActorRef(), selectVaultState);
  const vaultContext = useSelector(vaultRef ?? emptyActor, selectVaultContext);
  const provider = vaultContext.provider;

  // [MCV-1753] A notification click on the page already on screen is a new location with
  // `state.fromNotification`; keying the fetch effect on it refetches every card once. A first
  // mount from a plain navigation leaves the key null, so it still fetches exactly once.
  // The key is latched: it only ever moves to the key of a NEW notification click. Following the
  // location instead would flip it back to null on the next plain navigation (a hash or search
  // replace, say) and re-run the whole fan-out for nothing. A POP is never a click (the history
  // entry keeps its state, so back/forward and a reload replay it), see `isNotificationArrival`.
  const location = useLocation();
  const navigationType = useNavigationType();
  const arrivalKey = isNotificationArrival(location.state, navigationType) ? location.key : null;
  const [notificationRefreshKey, setNotificationRefreshKey] = useState(arrivalKey);
  if (arrivalKey !== null && arrivalKey !== notificationRefreshKey) {
    setNotificationRefreshKey(arrivalKey);
  }

  // --- Local machines (Licenses, Education) ---
  const licenseRef = useActorRef(combinedLicenseMachine, { input: {} });
  const licenseCtx = useSelector(licenseRef, (s) => s.context);

  const eduRef = useActorRef(eduTrainingMachine, { input: {} });
  const eduCtx = useSelector(eduRef, (s) => s.context);

  // --- Parent vault machines ---
  const boardCertCrud = useSelector(
    vaultContext.boardCertRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context?.crudRef
  );
  const boardCertCtx = useSelector(
    boardCertCrud ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context ?? { data: [], loading: true, error: false }
  );

  const certCrud = useSelector(
    vaultContext.certRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context?.crudRef
  );
  const certCtx = useSelector(
    certCrud ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context ?? { data: [], loading: true, error: false }
  );

  const cmeCtx = useSelector(
    vaultContext.cmeTrainingRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context ?? { data: [], loading: true, error: false }
  );

  const documentsCtx = useSelector(
    vaultContext.documentsService ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context ?? { documentReferences: [], loading: true, error: false }
  );

  const privCrud = useSelector(
    vaultContext.privilegesRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context?.crudRef
  );
  const privCtx = useSelector(
    privCrud ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context ?? { data: [], loading: true, error: false }
  );

  const employments: EmploymentRecord[] = useSelector(
    vaultContext.employmentRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context?.employments ?? []
  );
  const employmentLoading = useSelector(
    vaultContext.employmentRef ?? emptyActor,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any) => s?.context?.loading ?? true
  );

  // Refs to capture the vault and its context for the fetch effect without causing re-runs
  const vaultContextRef = useRef(vaultContext);
  vaultContextRef.current = vaultContext;
  const vaultRefRef = useRef(vaultRef);
  vaultRefRef.current = vaultRef;

  // --- Fire all fetches on mount / when provider changes ---
  useEffect(() => {
    if (!provider) return;
    const ctx = vaultContextRef.current;
    licenseRef.send({ type: 'ON_GET', providerId: provider.id });
    eduRef.send({ type: 'ON_LOAD', providerId: provider.id });
    ctx.boardCertRef?.send({ type: 'ON_LOAD' });
    ctx.certRef?.send({ type: 'ON_LOAD' });
    ctx.cmeTrainingRef?.send({ type: 'ON_LOAD' });
    // A notification refresh must see the change that raised the alert, so it bypasses the
    // documents service's five-minute cache, and waits for the service when it is busy (it takes
    // GET_DOCUMENTS only when idle). An ordinary visit keeps the cache.
    const documentsRefresher = createForcedDocumentsRefresher();
    const vault = vaultRefRef.current;
    if (notificationRefreshKey !== null && vault) {
      documentsRefresher.request(vault);
    } else {
      ctx.documentsService?.send({ type: 'GET_DOCUMENTS', providerId: provider.id });
    }
    ctx.privilegesRef?.send({ type: 'ON_LOAD' });
    ctx.employmentRef?.send({ type: 'ON_LOAD' });
    // MCV-1413: prime medLicense/licenses refs read by destination pages; CategoryCard
    // only navigates, so the vault state machine's entry-action ON_LOAD never fires.
    ctx.medLicenseRef?.send({ type: 'ON_LOAD' });
    ctx.licensesRef?.send({ type: 'ON_LOAD' });
    // licenseRef/eduRef are stable refs from useActorRef for this component's lifetime,
    // so they intentionally don't belong in deps — only re-fire when the provider changes or a
    // notification click asks for a refresh.
    // A deferred documents refresh must not outlive the fan-out that queued it.
    return documentsRefresher.cancel;
  }, [provider?.id, notificationRefreshKey]);

  // --- Build category summaries ---
  const categories: CategorySummary[] = useMemo(() => {
    const allLicenses = licenseCtx.data ?? [];
    const medicalLicenseData = allLicenses.filter(
      (l) => l.keyPrefix === 'MED-' || l.licenseType === 'MEDICAL'
    );
    const deaCdsLicenseData = allLicenses.filter(
      (l) => l.keyPrefix === 'DEA-' || l.keyPrefix === 'CDS-'
    );

    const formatExpiry = (expiryDate?: string) => {
      if (!expiryDate) return 'No Expiry';
      const d = new Date(expiryDate);
      if (Number.isNaN(d.getTime())) return 'No Expiry';
      return formatMonthYear(d);
    };

    const medicalLicenses = buildExpirableCategory(
      'medicalLicenses',
      'Medical Licenses',
      PROVIDER_PAGE_KEYS.medicalLicense,
      medicalLicenseData,
      licenseCtx.loading,
      licenseCtx.error,
      'licenseType',
      'expiryDate',
      {
        columnsForItem: (item) => {
          const stateCode = item?.stateCode && item.stateCode !== 'NA' ? item.stateCode : '';
          const type = item?.licenseType || '';
          const label = type && stateCode ? `${type} - ${stateCode}` : type || '--';
          return [label, item?.licenseNumber || '--', formatExpiry(item?.expiryDate)];
        },
      }
    );
    medicalLicenses.columnDefs = [{ label: 'License' }, { label: 'Number' }, { label: 'Expires' }];
    // [MCV-1812] The loaded records behind each card, per vault table: the "New" pill needs an
    // unseen id that is really in the list (a record deleted before it was seen stays unseen).
    medicalLicenses.recordIdsByTable = {
      [NEW_RECORD_TABLE.medicalLicenses]: idsOf(medicalLicenseData),
    };

    const deaCdsLicenses = buildExpirableCategory(
      'deaCdsLicenses',
      'DEA/CDS Licenses',
      // Explicit first-tab hash so the page can't land on a stale tab (MCV-1464)
      `${PROVIDER_PAGE_KEYS.licenses}#dea`,
      deaCdsLicenseData,
      licenseCtx.loading,
      licenseCtx.error,
      'licenseType',
      'expiryDate',
      {
        columnsForItem: (item) => {
          const state = item?.stateCode && item.stateCode !== '--' ? item.stateCode : '--';
          return [item?.licenseType || '--', state, formatExpiry(item?.expiryDate)];
        },
      }
    );
    deaCdsLicenses.columnDefs = [{ label: 'Type' }, { label: 'State' }, { label: 'Expires' }];
    // DEA and CDS share this list; ids repeat across the two tables, so split by key prefix.
    deaCdsLicenses.recordIdsByTable = {
      [NEW_RECORD_TABLE.deaLicenses]: idsOf(
        deaCdsLicenseData.filter((l) => l.keyPrefix === 'DEA-')
      ),
      [NEW_RECORD_TABLE.cdsLicenses]: idsOf(
        deaCdsLicenseData.filter((l) => l.keyPrefix === 'CDS-')
      ),
    };

    const boardCerts = buildExpirableCategory(
      'boardCerts',
      'Board Certifications',
      PROVIDER_PAGE_KEYS.boardCertifications,
      boardCertCtx.data ?? [],
      boardCertCtx.loading ?? true,
      boardCertCtx.error ?? false,
      'certName',
      'expiryDate',
      {
        deriveStatus: deriveBoardCertStatus,
        columnsForItem: (item) => {
          const certLabel =
            typeof item?.certName === 'object' ? item.certName?.label : item?.certName;
          // Use `_tblBoardName` (computed by getBoardAbbr) instead of the raw
          // `boardName` column. Certs linked only via specialty_board_id leave
          // the flat `boardName` NULL; `_tblBoardName` walks the nested
          // specialtyBoard/medicalBoard relationships and falls back to the
          // full board name, matching what the vault grid displays.
          return [certLabel || '--', item?._tblBoardName || '--', formatExpiry(item?.expiryDate)];
        },
      }
    );
    boardCerts.columnDefs = [{ label: 'Certification' }, { label: 'Board' }, { label: 'Expires' }];
    boardCerts.recordIdsByTable = {
      [NEW_RECORD_TABLE.boardCertifications]: idsOf(boardCertCtx.data),
    };
    // Certifications intentionally don't opt into deriveStatus or unknown-state
    // handling. A null expiry_date means "no expiration" by convention — see
    // docs/superpowers/specs/2026-04-18-dashboard-no-expiration-state-design.md
    // for the decision record. Changing this without that spec's context will
    // regress legitimate permanent certifications into dashboard warnings.
    const certs = buildExpirableCategory(
      'certifications',
      'Certifications',
      PROVIDER_PAGE_KEYS.certifications,
      certCtx.data ?? [],
      certCtx.loading ?? true,
      certCtx.error ?? false,
      'certName',
      'expiryDate',
      {
        columnsForItem: (item) => {
          const certLabel =
            typeof item?.certName === 'object' ? item.certName?.label : item?.certName;
          const issuerLabel = typeof item?.issuer === 'object' ? item.issuer?.label : item?.issuer;
          return [certLabel || '--', issuerLabel || '--', formatExpiry(item?.expiryDate)];
        },
      }
    );
    certs.columnDefs = [{ label: 'Certification' }, { label: 'Issuer' }, { label: 'Expires' }];
    certs.recordIdsByTable = { [NEW_RECORD_TABLE.certifications]: idsOf(certCtx.data) };
    const formatGrad = (gradDate?: string) => {
      if (!gradDate || gradDate === 'Incomplete') return 'Incomplete';
      return formatCredentialSummaryDate(gradDate);
    };
    const recentAcademics = getMostRecentAcademicRecords(eduCtx.data);
    const education = buildNeutralCategory(
      'education',
      'Education and Training',
      // Explicit first-tab hash so the page can't land on a stale tab (MCV-1464)
      `${PROVIDER_PAGE_KEYS.academics}#education`,
      recentAcademics,
      eduCtx.loading,
      eduCtx.error,
      'schoolName',
      (item) => formatGrad(item.gradDate),
      (item) => [item.schoolName || '--', item.degreeName || '--', formatGrad(item.gradDate)]
    );
    education.count = eduCtx.data.length;
    education.statusText = `${eduCtx.data.length} ${eduCtx.data.length === 1 ? 'record' : 'records'}`;
    education.columnDefs = [
      { label: 'Institution' },
      { label: 'Degree / Training' },
      { label: 'Graduated' },
    ];
    // Education and training share this list; split by key prefix (ids repeat across tables).
    education.recordIdsByTable = {
      [NEW_RECORD_TABLE.education]: idsOf(eduCtx.data.filter((r) => r.keyPrefix === 'EDU-')),
      [NEW_RECORD_TABLE.training]: idsOf(eduCtx.data.filter((r) => r.keyPrefix === 'TRN-')),
    };
    const formatEmploymentDate = (date?: string | null) => {
      if (!date) return 'Present';
      return formatCredentialSummaryDate(date);
    };
    const employmentPosition = (item: EmploymentRecord) => {
      const apptType =
        typeof item.apptType === 'object' ? item.apptType?.label : (item.apptType ?? '');
      return [item.title, item.department, apptType].filter(Boolean).join(' / ') || '--';
    };
    const recentEmployments = getMostRecentEmploymentRecords(employments);
    const employment = buildNeutralCategory(
      'employment',
      'Employment',
      PROVIDER_PAGE_KEYS.employment,
      recentEmployments,
      employmentLoading,
      false,
      'employer',
      (item) =>
        `${formatEmploymentDate(item.startDate)} - ${
          item.currentFlag || !item.endDate ? 'Present' : formatEmploymentDate(item.endDate)
        }`,
      (item) => [
        item.employer || '--',
        employmentPosition(item),
        `${formatEmploymentDate(item.startDate)} - ${
          item.currentFlag || !item.endDate ? 'Present' : formatEmploymentDate(item.endDate)
        }`,
      ]
    );
    employment.count = employments.length;
    employment.statusText = `${employments.length} ${employments.length === 1 ? 'record' : 'records'}`;
    employment.columnDefs = [
      { label: 'Employer' },
      { label: 'Position', hideAtNarrowWidth: true },
      { label: 'Dates' },
    ];
    employment.recordIdsByTable = { [NEW_RECORD_TABLE.employment]: idsOf(employments) };
    const formatCredits = (creditsEarned?: number | string) =>
      creditsEarned ? `${creditsEarned} hrs` : '--';
    const cme = buildNeutralCategory(
      'cmeTraining',
      'CME Credits',
      PROVIDER_PAGE_KEYS.cmeTraining,
      cmeCtx.data ?? [],
      cmeCtx.loading ?? true,
      cmeCtx.error ?? false,
      'courseTitle',
      (item) => formatCredits(item.creditsEarned),
      (item) => {
        const creditType =
          typeof item.creditType === 'object' ? item.creditType?.label : item.creditType;
        return [item.courseTitle || '--', creditType || '--', formatCredits(item.creditsEarned)];
      }
    );
    cme.columnDefs = [{ label: 'Course' }, { label: 'Credit Type' }, { label: 'Credits' }];
    cme.recordIdsByTable = { [NEW_RECORD_TABLE.cmeTrainings]: idsOf(cmeCtx.data) };
    const formatUploaded = (uploadedAt?: string) => {
      if (!uploadedAt) return '--';
      const d = new Date(uploadedAt);
      return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };
    const documents = buildNeutralCategory(
      'documents',
      'Documents',
      PROVIDER_PAGE_KEYS.documents,
      documentsCtx.documentReferences ?? [],
      documentsCtx.loading ?? true,
      documentsCtx.error ?? false,
      'docName',
      (item) => formatUploaded(item.uploadedAt),
      (item) => [item.docName || '--', item.docDesc || '--', formatUploaded(item.uploadedAt)]
    );
    documents.columnDefs = [
      { label: 'Name' },
      { label: 'Description', hideAtNarrowWidth: true },
      { label: 'Uploaded' },
    ];
    // [MCV-1812] Documents have no parse-created records; the card shows New on the upload day,
    // the same rule as the Documents list pill.
    documents.hasUploadToday = (documentsCtx.documentReferences ?? []).some(
      (doc: { uploadedAt?: string | null }) => isUploadedToday(doc.uploadedAt)
    );
    const privData = privCtx.data ?? [];
    const sortedPrivData = sortPrivileges(privData, 'specialtyName');
    const privItems: SummaryItem[] = sortedPrivData.slice(0, 5).map((item) => {
      const spec =
        typeof item.specialtyName === 'object' ? item.specialtyName?.label : item.specialtyName;
      return {
        name: spec || '--',
        badge: '',
        badgeVariant: 'neutral' as const,
        columns: [spec || '--', item.privilegeName || '--', item.privilegeType || '--'],
      };
    });
    const privileges: CategorySummary = {
      key: 'privileges',
      name: 'Privileges',
      count: privData.length,
      status: 'neutral',
      statusText: `${privData.length} on file`,
      expiredCount: 0,
      expiringCount: 0,
      inactiveCount: 0,
      items: privItems,
      columnDefs: [{ label: 'Specialty' }, { label: 'Privilege' }, { label: 'Type' }],
      loading: privCtx.loading ?? true,
      error: privCtx.error ?? false,
      navigateTo: `${PROVIDER_BASE}/${PROVIDER_PAGE_KEYS.privileges}`,
      icon: getVaultTopicIcon(PROVIDER_PAGE_KEYS.privileges),
      fullWidth: true,
      recordIdsByTable: { [NEW_RECORD_TABLE.privileges]: idsOf(privData) },
    };

    const showPrivileges = isPrivilegesApplicable(provider?.category?.id);

    return [
      medicalLicenses,
      certs,
      boardCerts,
      education,
      deaCdsLicenses,
      employment,
      cme,
      documents,
      ...(showPrivileges ? [privileges] : []),
    ];
  }, [
    licenseCtx,
    boardCertCtx,
    certCtx,
    eduCtx,
    employments,
    employmentLoading,
    cmeCtx,
    documentsCtx,
    privCtx,
    provider?.category?.id,
  ]);

  // --- Derive aggregates ---
  const loading = categories.some((c) => c.loading);
  const employmentGapScanStartDate = useMemo(
    () => getEarliestOriginalCertificationDate(boardCertCtx.data ?? []),
    [boardCertCtx.data]
  );

  const alerts = useMemo((): AlertItem[] => {
    const base = computeAlerts(categories);
    const categoryId = provider?.category?.id;
    if (
      categoryId != null &&
      EMPLOYMENT_GAP_CATEGORY_IDS.includes(categoryId) &&
      detectEmploymentGaps(employments, employmentGapScanStartDate)
    ) {
      base.push({
        category: 'Employment',
        message: 'Gap of more than 30 days detected',
        navigateTo: `${PROVIDER_BASE}/${PROVIDER_PAGE_KEYS.employment}`,
      });
    }
    return base;
  }, [categories, provider, employments, employmentGapScanStartDate]);

  const completeness = useMemo(
    () => computeCompleteness(categories.map((c) => ({ name: c.name, count: c.count }))),
    [categories]
  );

  const quickStats = useMemo(() => {
    let expired = 0;
    let expiringSoon = 0;
    let inactive = 0;
    for (const c of categories) {
      expired += c.expiredCount;
      expiringSoon += c.expiringCount;
      inactive += c.inactiveCount;
    }
    const total = categories.reduce((s, c) => s + c.count, 0);
    const active = total - expired - expiringSoon - inactive;
    return computeQuickStats(total, active, expiringSoon, expired, inactive);
  }, [categories]);

  return { loading, alerts, completeness, quickStats, categories, providerId: provider?.id };
};
